import { Device, DeviceType, isDeviceType } from "@domain/product/monitoring/device";
import { FamilyStep } from "@domain/product/monitoring/family/family-step";
import { FamilyStepOption } from "@domain/product/monitoring/family/family-step-option";
import { getConfigurationOptionIdentifier } from "@project/monitoring/monitoring-configuration.utils";

enum OptionalStepAssignment {
  ADDITIONAL = "additional",
  ACCESSORY = "accessory",
  MIXED = "mixed",
  EMPTY = "empty",
}

export interface CentralStationOptionalOptionBuckets {
  additionalLicences: FamilyStepOption[];
  accessories: FamilyStepOption[];
}

export function isSameCentralStationOption(
  familyName: string | undefined,
  currentOption: FamilyStepOption,
  storedOption: FamilyStepOption,
): boolean {
  const currentDevices = [...(currentOption.devices ?? [])].map(String).sort().join("|");
  const storedDevices = [...(storedOption.devices ?? [])].map(String).sort().join("|");

  if (currentDevices && storedDevices) {
    return currentDevices === storedDevices;
  }

  return (
    getConfigurationOptionIdentifier(familyName, currentOption) === getConfigurationOptionIdentifier(familyName, storedOption)
  );
}

export function appendDeviceTypes(map: Map<string, DeviceType>, devices: Device[]) {
  if (!map || !devices?.length) return;

  devices
    .filter((device): device is Device => !!device && device.matNr !== undefined && device.matNr !== null)
    .filter((device): device is Device & { deviceType: DeviceType } => isDeviceType(device.deviceType))
    .forEach((device) => map.set(String(device.matNr), device.deviceType));
}

export function buildDeviceTypeMap(devices: Device[]) {
  const map = new Map<string, DeviceType>();
  appendDeviceTypes(map, devices);

  return map;
}

export function resolveOptionalOptionBuckets(
  pick0Steps: FamilyStep[],
  fallbackStep: FamilyStep | undefined,
  deviceTypesByMatNr: Map<string, DeviceType>,
  additionalTypes: DeviceType[],
  accessoryTypes: DeviceType[],
): CentralStationOptionalOptionBuckets {
  const steps = pick0Steps?.length ? pick0Steps : fallbackStep ? [fallbackStep] : [];
  if (!steps.length) return emptyBuckets();

  if (steps.length === 1) {
    return splitOptionsByDeviceType(steps[0].options ?? [], deviceTypesByMatNr, additionalTypes, accessoryTypes);
  }

  return resolveBucketsFromMultipleSteps(steps, deviceTypesByMatNr, additionalTypes, accessoryTypes);
}

export function splitOptionsByDeviceType(
  options: FamilyStepOption[],
  deviceTypesByMatNr: Map<string, DeviceType>,
  additionalTypes: DeviceType[],
  accessoryTypes: DeviceType[],
): CentralStationOptionalOptionBuckets {
  const additionalTypeSet = new Set(additionalTypes);
  const accessoryTypeSet = new Set(accessoryTypes);
  const safeOptions = options ?? [];
  const additionalLicences: FamilyStepOption[] = [];
  const accessories: FamilyStepOption[] = [];
  const unknown: FamilyStepOption[] = [];

  safeOptions.forEach((option) => {
    const matchesAdditional = optionHasDeviceType(option, deviceTypesByMatNr, additionalTypeSet);
    const matchesAccessory = optionHasDeviceType(option, deviceTypesByMatNr, accessoryTypeSet);

    if (matchesAdditional && !matchesAccessory) {
      additionalLicences.push(option);
      return;
    }

    if (matchesAccessory && !matchesAdditional) {
      accessories.push(option);
      return;
    }

    unknown.push(option);
  });

  assignUnknownOptions(additionalLicences, accessories, unknown);
  return { additionalLicences, accessories };
}

function optionHasDeviceType(option: FamilyStepOption, deviceTypesByMatNr: Map<string, DeviceType>, types: Set<DeviceType>) {
  const devices = option?.devices ?? [];

  return devices.some((device) => {
    const type = deviceTypesByMatNr.get(String(device));
    return type ? types.has(type) : false;
  });
}

function emptyBuckets(): CentralStationOptionalOptionBuckets {
  return { additionalLicences: [], accessories: [] };
}

function resolveBucketsFromMultipleSteps(
  steps: FamilyStep[],
  deviceTypesByMatNr: Map<string, DeviceType>,
  additionalTypes: DeviceType[],
  accessoryTypes: DeviceType[],
): CentralStationOptionalOptionBuckets {
  const additionalTypeSet = new Set(additionalTypes);
  const accessoryTypeSet = new Set(accessoryTypes);
  const assignments = steps.map((step) => classifyStep(step, deviceTypesByMatNr, additionalTypeSet, accessoryTypeSet));
  const hasMixed = assignments.some((assignment) => assignment === OptionalStepAssignment.MIXED);

  if (!hasMixed) {
    const additionalLicences: FamilyStepOption[] = [];
    const accessories: FamilyStepOption[] = [];

    assignments.forEach((assignment, index) => {
      if (assignment === OptionalStepAssignment.ADDITIONAL) {
        additionalLicences.push(...(steps[index].options ?? []));
      } else if (assignment === OptionalStepAssignment.ACCESSORY) {
        accessories.push(...(steps[index].options ?? []));
      }
    });
    return { additionalLicences, accessories };
  }

  const options = steps.flatMap((step) => step.options ?? []);
  return splitOptionsByDeviceType(options, deviceTypesByMatNr, additionalTypes, accessoryTypes);
}

function classifyStep(
  step: FamilyStep,
  deviceTypesByMatNr: Map<string, DeviceType>,
  additionalTypeSet: Set<DeviceType>,
  accessoryTypeSet: Set<DeviceType>,
): OptionalStepAssignment {
  const options = step?.options ?? [];
  if (!options.length) return OptionalStepAssignment.EMPTY;

  let hasAdditional = false;
  let hasAccessory = false;
  let hasUnknown = false;

  for (const option of options) {
    const assignment = classifyOption(option, deviceTypesByMatNr, additionalTypeSet, accessoryTypeSet);

    if (assignment === OptionalStepAssignment.ADDITIONAL) {
      hasAdditional = true;
    } else if (assignment === OptionalStepAssignment.ACCESSORY) {
      hasAccessory = true;
    } else {
      hasUnknown = true;
    }

    if (hasUnknown || (hasAdditional && hasAccessory)) return OptionalStepAssignment.MIXED;
  }

  if (hasAdditional) return OptionalStepAssignment.ADDITIONAL;
  if (hasAccessory) return OptionalStepAssignment.ACCESSORY;

  return OptionalStepAssignment.MIXED;
}

function assignUnknownOptions(
  additionalLicences: FamilyStepOption[],
  accessories: FamilyStepOption[],
  unknown: FamilyStepOption[],
) {
  if (!unknown.length) return;

  if (additionalLicences.length === 0 && accessories.length > 0) {
    additionalLicences.push(...unknown);
    return;
  }

  if (accessories.length === 0 && additionalLicences.length > 0) {
    accessories.push(...unknown);
    return;
  }

  additionalLicences.push(...unknown);
}

function classifyOption(
  option: FamilyStepOption,
  deviceTypesByMatNr: Map<string, DeviceType>,
  additionalTypeSet: Set<DeviceType>,
  accessoryTypeSet: Set<DeviceType>,
): OptionalStepAssignment {
  const matchesAdditional = optionHasDeviceType(option, deviceTypesByMatNr, additionalTypeSet);
  const matchesAccessory = optionHasDeviceType(option, deviceTypesByMatNr, accessoryTypeSet);

  if (matchesAdditional && !matchesAccessory) return OptionalStepAssignment.ADDITIONAL;
  if (matchesAccessory && !matchesAdditional) return OptionalStepAssignment.ACCESSORY;

  return OptionalStepAssignment.MIXED;
}
